import {
  EDSApplication, ErrorPage as EDSErrorPage, makeStyles, TextLink, Typography,
} from '@ellucian/react-design-system/core/es';
import { createEDSTheme } from '@ellucian/react-design-system/core/es/themes';
import { spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import ctaColors from '@ellucian/react-design-system/core/themes/common/ctaColors';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import validUrl from 'valid-url';
import PageLoader from '../dashboardApp/components/PageLoader';

const useStyles = makeStyles(() => ({
  illustration: {
    '& img': {
      height: 'calc(60vh)',
    },
  },
  root: {
    '& header nav div': {
      maxWidth: 'unset !important',
    },
    height: 'calc(92vh) !important',
  },
  rootWithoutHeader: {
    height: 'calc(65vh) !important',
  },
  customLogo: {
    padding: '0.85rem',
    maxWidth: '100%',
    maxHeight: '100%',
  },
  logo: {
    marginTop: '-1rem',
    width: `calc(5.625rem + ${spacing40} + ${spacing40})`,
    '@supports (padding-top: env(safe-area-inset-left))': {
      width: `calc(5.625rem + ${spacing40} + ${spacing40} + env(safe-area-inset-left))`,
    },
  },
}));

const ID = 'dash';

const ErrorPage = ({ errorCode, noHeader = false, messages = undefined, noAction = false }) => {

  const [i18Message, setI18Message] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorType, setErrorType] = useState('');
  const [theme, setTheme] = useState(undefined);
  const [customLogoUrl, setCustomLogoUrl] = useState(undefined);
  const [themeSetting, setThemeSetting] = useState({});
  const classes = useStyles();

  useEffect(() => {
    init();
    if (!errorCode?.length > 0) {
      switch (getErrorCode() || '') {
        case '400': setErrorType('400');
          break;
        case '401': setErrorType('401');
          break;
        case '404': setErrorType('404');
          break;
        case '405': setErrorType('405');
          break;
        case '500': setErrorType('500');
          break;
        case '503': setErrorType('503');
          break;
        default: setErrorType('403');
      }
      window.history.pushState(null, 'Error Page', `${getBaseURL()}error`);
    } else {
      setErrorType(errorCode);
    }
  }, []);

  const getBaseURL = () => {
    let baseURL = document.getElementById('contextPath')?.innerHTML || '';
    if (!baseURL.endsWith('/')) { baseURL += '/'; }
    return baseURL;
  };

  const init = () => {
    if (messages) {
      setI18Message(messages);
      setIsLoading(false);
    } else {
      Promise.all([fetch(`${getBaseURL()}api/messages/dash.errorPage`)
        .then(response => response.json())
        .then(result => {
          setI18Message(result);
        }),
      fetch(`${getBaseURL()}api/settings/theme`)
        .then(response => {
          if (!response.ok) {
            throw response;
          }
          return response.json();
        })
        .then(settings => {
          setThemeSetting(settings);
        })]);
    }
  };

  useEffect(() => {
    configureTheme();
  }, [themeSetting]);

  useEffect(() => {
    setIsLoading(false);
  }, [theme]);

  const getErrorCode = () => {
    return document.getElementById('errorStatusCode')?.innerHTML;
  };

  const isColorValid = (color) => {
    const hexRegex = new RegExp(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);

    if (hexRegex.test(color)) {
      return true;
    } else if (!color) {
      return true;
    } else {
      return false;
    }
  };

  const configureTheme = () => {
    const configuredOverrides = { palette: {} };
    const primaryColor = themeSetting['theme.color.primary'];
    const secondaryColor = themeSetting['theme.color.secondary'];
    const tertiaryName = themeSetting['theme.color.tertiaryName'];
    const customLogoUrlParam = themeSetting['theme.url.logo'];
    const errors = [];
    if (validUrl.isWebUri(customLogoUrlParam)) {
      setCustomLogoUrl(customLogoUrlParam);
    }
    else {
      errors.push('Ignoring invalid customLogoUrl: ' + customLogoUrl);
    }
    if (primaryColor && primaryColor.length > 0) {
      if (isColorValid(primaryColor)) {
        configuredOverrides.palette.primary = { main: primaryColor };
      }
      else {
        errors.push('Ignoring invalid primaryColor: ' + primaryColor);
      }
    }
    if (secondaryColor && secondaryColor.length > 0) {
      if (isColorValid(secondaryColor)) {
        configuredOverrides.palette.secondary = { main: secondaryColor };
      }
      else {
        errors.push('Ignoring invalid secondaryColor: ' + primaryColor);
      }
    }
    if (tertiaryName && tertiaryName.length > 0) {
      configuredOverrides.palette.ctaColorName = tertiaryName;
    }

    if (errors.length > 0) {
      console.group('Error page');
      errors.forEach(error => {
        console.info(error);
      });
      console.groupEnd();
    }

    updateTheme(configuredOverrides);
  };

  const updateTheme = (overrides) => {
    if (overrides === 'default') {
      setTheme(createEDSTheme('light'));
      return;
    }
    const configuredOverrides = { palette: {} };
    if (overrides.palette.primary) {
      configuredOverrides.palette.primary = { main: overrides.palette.primary.main };
    }
    if (overrides.palette.secondary) {
      configuredOverrides.palette.secondary = { main: overrides.palette.secondary.main };
    }
    if (overrides.palette.ctaColorName) {
      configuredOverrides.palette.ctaColor = { ...ctaColors[overrides.palette.ctaColorName.toLowerCase()] };
    }
    const newTheme = createEDSTheme('light', configuredOverrides);
    setTheme(newTheme);
  };

  const ACTIONS = {
    400: <></>,
    401: <TextLink id={`${ID}-action-link`}
      href={getBaseURL()}
    >
      <Typography>{i18Message?.['dash.errorPage.authenticate.signIn.label']}</Typography>
    </TextLink>,
    403: <></>,
    404: <TextLink id={`${ID}-action-link`}
      href={getBaseURL()}
    >
      <Typography>{i18Message?.['dash.errorPage.pageNotFound.home.label']}</Typography>
    </TextLink>,
    405: <></>,
    500: <></>,
    503: <></>,
  };

  return (
    <EDSApplication
      id={`${ID}-container`}
      locale="en"
      theme={theme}
      labelOverrides={
        i18Message !== undefined && Object.keys(i18Message).length > 0
          ? {
            'component.ErrorPage.400': i18Message['dash.errorPage.badRequest'],
            'component.ErrorPage.400.message': i18Message['dash.errorPage.badRequest.message'],
            'component.ErrorPage.401': i18Message['dash.errorPage.authenticate'],
            'component.ErrorPage.401.message': i18Message['dash.errorPage.authenticate.message'],
            'component.ErrorPage.403': i18Message['dash.errorPage.accessDenied'],
            'component.ErrorPage.403.message': i18Message['dash.errorPage.accessDenied.message'],
            'component.ErrorPage.404': i18Message['dash.errorPage.pageNotFound'],
            'component.ErrorPage.404.message': i18Message['dash.errorPage.pageNotFound.message'],
            'component.ErrorPage.405': i18Message['dash.errorPage.methodNotAllowed'],
            'component.ErrorPage.405.message': i18Message['dash.errorPage.methodNotAllowed.message'],
            'component.ErrorPage.500': i18Message['dash.errorPage.internalServerError'],
            'component.ErrorPage.500.message': i18Message['dash.errorPage.internalServerError.message'],
            'component.ErrorPage.503': i18Message['dash.errorPage.serviceUnavailable'],
            'component.ErrorPage.503.message': i18Message['dash.errorPage.serviceUnavailable.message'],
          }
          : undefined
      }
    >
      {isLoading || theme === undefined ? (
        <PageLoader id={`${ID}-loading`} centered={true} />
      ) : (
        <EDSErrorPage
          id={`${ID}-error-page`}
          errorType={errorType}
          action={noAction ? <></> : ACTIONS[errorType]}
          noHeader={noHeader || false}
          imageSrc={`images/${errorType}.png`}
          classes={{
            errorTemplate: classes.illustration,
            root: noHeader ? classes.rootWithoutHeader : classes.root,
            'headerbar-logo': classes.logoRoot,
          }}
          logo={
            customLogoUrl ? (
              <img
                src={customLogoUrl}
                className={classes.customLogo}
                alt={i18Message['dash.navigation.degreeworks']}
              />
            ) : undefined
          }
        />
      )}
    </EDSApplication>
  );
};

ErrorPage.propTypes = {
  noHeader: PropTypes.bool,
  noAction: PropTypes.bool,
  messages: PropTypes.object,
  errorCode: PropTypes.oneOf([
    'maintenance',
    '400',
    '401',
    '403',
    '404',
    '405',
    '500',
    '503',
  ]),
};

export default ErrorPage;
